import React from "react";
import { Controller, useForm } from "react-hook-form";
import styled from "styled-components";
import Image from "next/image";
import useIsResMobile from "lib/hooks/useIsResMobile";
import PageLayout from "lib/components/PageLayout";
import ContentArea from "lib/components/ContentArea";
import IconArrow from "lib/assets/arrow.svg";
import IconPinWhite from "lib/assets/pin-white.svg";
import ImgGradient from "lib/assets/gradient.jpg";
import ImgIllustrationCareer from "lib/assets/illustration-career.png";
import ImgWorldMap from "lib/assets/img-map.png";
import LogoWhite from "lib/components/LogoWhite";
import Button from "lib/components/Button";
import { useLayout } from "lib/contexts/layoutContext";
import TextField from "lib/components/TextField";
import { isValidEmail } from "domain/auth/isValidEmail";
import { SpinFixed } from "lib/components/Spin";
import Fetch from "lib/fetch";
import Head from "lib/components/Head";
import { useTranslations } from "lib/i18n";

const fetch = new Fetch();

const SectionTitle = styled.h1`
  margin-top: 0;
  font-size: 50px;
  line-height: 1em;
  font-weight: bold;
`;

const CareerPostRow = styled.div`
  padding: 22px;
  height: 70px;
  border-radius: 10px;
  background-color: #ffffff;
  margin-bottom: 10px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.25s ease-in;
  color: black;
  &:hover {
    color: #195dee;
    box-shadow: 3px 3px 8px 0 #d2d6e4;
  }
`;

const SectionWrapper = styled.section``;

const Section = ({
  children,
  background,
  id,
}: {
  children: React.ReactNode;
  background: string;
  id?: string;
}) => {
  const isResMobile = useIsResMobile();
  return (
    <SectionWrapper
      id={id}
      css={`
        background-color: ${background};
        padding: ${isResMobile ? `40px 0` : `80px 0`};
      `}
    >
      {children}
    </SectionWrapper>
  );
};

const IconArrowBlack = () => (
  <IconArrow
    stroke="black"
    css={`
      float: right;
    `}
  />
);

const About = () => {
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    reset,
  } = useForm<{
    name: string;
    email: string;
    subject: string;
    contents: string;
  }>({
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      contents: "",
    },
  });
  const { doOpenAlert } = useLayout();

  const handleOnSubmit = handleSubmit(
    async ({ name, email, subject, contents }) => {
      try {
        await submitForm();
        doOpenAlert({
          content: t("contact.success"),
          onConfirm: () => reset(),
        });
        // RESET FORM
      } catch (e) {
        console.error("form submit error", e);
      }

      async function submitForm() {
        return await fetch.post("https://formspree.io/f/mayqgzjv", {
          name,
          email,
          subject,
          contents,
        });
      }
    },
  );

  const isResMobile = useIsResMobile();

  const t = useTranslations("about");

  return (
    <PageLayout>
      {isSubmitting && <SpinFixed />}
      <Head title={t("meta.title")} desc={t("meta.description")} url="/about" />

      {/* Hero */}

      <div
        css={`
          height: 500px;
          background-color: #f1f3f5;
          background-size: 100% 100%;
          background-position: center;
          background-image: url(${ImgGradient});
        `}
      >
        <ContentArea>
          <Flex
            css={`
              height: 100%;
              justify-content: center;
              flex-direction: column;
              align-items: center;
            `}
          >
            <img
              src="https://lawfully-web-static-resources.s3.us-east-1.amazonaws.com/lawfully-full-logo-dark.svg"
              alt={t("hero.logoAlt")}
              css={`
                height: 75px;
              `}
            />
            <p
              css={`
                font-size: ${isResMobile ? `18px` : `28px`};
                width: 100%;
                font-weight: 500;
                text-align: center;
                color: white;
                line-height: 1.25;
                margin: 40px 0 0;
              `}
            >
              {t("hero.tagline")}
            </p>
          </Flex>
        </ContentArea>
      </div>

      {/* About us */}

      <Section id="about" background="white">
        <ContentArea>
          <SectionTitle
            css={`
              font-size: ${isResMobile ? `26px` : `50px`};
            `}
          >
            {t("company.title")}
          </SectionTitle>
          <Flex
            css={`
              flex-wrap: wrap;
            `}
          >
            <p
              css={`
                width: 100%;
                font-size: ${isResMobile ? `14px` : `18px`};
                font-weight: 500;
                line-height: 1.67;
                white-space: pre-line;
              `}
            >
              {[
                t("company.paragraph1"),
                t("company.paragraph2"),
                t("company.paragraph3"),
              ].join("\n\n")}
            </p>
          </Flex>
        </ContentArea>
      </Section>

      {/* Career */}

      <Section id="careers" background="#f3f4f8">
        <ContentArea>
          <SectionTitle
            css={`
              font-size: ${isResMobile ? `26px` : `50px`};
            `}
          >
            {t("careers.title")}
          </SectionTitle>
          <Flex
            css={`
              flex-wrap: wrap;
              justify-content: space-between;
            `}
          >
            <p
              css={`
                width: 424px;
                font-size: ${isResMobile ? `14px` : `18px`};
                font-weight: 500;
                line-height: 1.67;
              `}
            >
              {t("careers.description")}
            </p>
            <Flex
              css={`
                flex-shrink: 1;
                flex-grow: 1;
                justify-content: flex-end;
              `}
            >
              <div
                css={`
                  width: ${isResMobile ? "180px" : "300px"};
                  height: auto;
                `}
              >
                <Image
                  src={ImgIllustrationCareer}
                  css={`
                    position: relative;
                    transform: scale(-1, 1);
                  `}
                  width={601}
                  height={586}
                />
              </div>
            </Flex>
          </Flex>
          <a
            href="https://angel.co/company/lawfully-1/jobs/2029197-head-of-growth"
            target="_blank"
            rel="noopener noreferrer"
          >
            <CareerPostRow
              css={`
                font-size: ${isResMobile ? `16px` : `18px`};
                line-height: ${isResMobile ? `1.5` : `1.56`};
              `}
            >
              Head of Growth
              <IconArrowBlack />
            </CareerPostRow>
          </a>
          <a
            href="https://angel.co/company/lawfully-1/jobs/1373766-product-manager"
            target="_blank"
            rel="noopener noreferrer"
          >
            <CareerPostRow
              css={`
                font-size: ${isResMobile ? `16px` : `18px`};
                line-height: ${isResMobile ? `1.5` : `1.56`};
              `}
            >
              Product Manager
              <IconArrowBlack />
            </CareerPostRow>
          </a>
        </ContentArea>
      </Section>

      {/* Office */}

      <Section id="office" background="#195dee">
        <ContentArea>
          <SectionTitle
            css={`
              color: white;
              font-size: ${isResMobile ? `26px` : `50px`};
            `}
          >
            {t("office.title")}
          </SectionTitle>
          <Flex
            css={`
              justify-content: center;
              position: relative;
            `}
          >
            <Image width={1618} height={854} src={ImgWorldMap} />
            <Flex
              css={`
                position: absolute;
                left: 25.5%;
                top: ${isResMobile ? `26%` : `36%`};
                align-items: center;
              `}
            >
              <IconPinWhite
                css={`
                  margin-right: 5px;
                `}
              />
              <span
                css={`
                  color: white;
                  font-size: 14px;
                  font-weight: bold;
                `}
              >
                {t("office.newYork")}
              </span>
            </Flex>
            <Flex
              css={`
                position: absolute;
                left: 82%;
                top: ${isResMobile ? `32%` : `39%`};
                align-items: center;
              `}
            >
              <IconPinWhite
                css={`
                  margin-right: 5px;
                `}
              />
              <span
                css={`
                  color: white;
                  font-size: 14px;
                  font-weight: bold;
                `}
              >
                {t("office.seoul")}
              </span>
            </Flex>
          </Flex>
        </ContentArea>
      </Section>

      {/* Contact us */}

      <Section id="contact" background="white">
        <ContentArea>
          <Flex
            css={`
              flex-wrap: wrap;
              justify-content: space-between;
            `}
          >
            <div
              css={`
                flex-basis: 50%;
                flex-shrink: 1;
              `}
            >
              <SectionTitle
                css={`
                  font-size: ${isResMobile ? `26px` : `50px`};
                `}
              >
                {t("contact.title")}
              </SectionTitle>
              <p
                css={`
                  max-width: ${isResMobile ? `100%` : `424px`};
                  font-size: 18px;
                  font-weight: 500;
                  line-height: 1.67;
                `}
              >
                {t("contact.description")}{" "}
                <a href="mailto: contact@lawfully.com">
                  <span
                    css={`
                      text-decoration: underline;
                    `}
                  >
                    contact@lawfully.com
                  </span>
                </a>
              </p>
            </div>
            <form
              css={`
                flex-basis: ${isResMobile ? `100%` : `50%`};
                flex-shrink: 1;
                flex-grow: 1;
              `}
              onSubmit={handleOnSubmit}
            >
              <Controller
                name="name"
                control={control}
                rules={{
                  required: true,
                }}
                render={(props) => (
                  <StyledTextField
                    {...props}
                    fullWidth
                    placeholder={t("contact.namePlaceholder")}
                    error={!!errors.name}
                    helperText={errors?.name?.message}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                rules={{
                  required: true,
                  validate: (val) =>
                    isValidEmail(val) ? true : t("contact.invalidEmail"),
                }}
                render={(props) => (
                  <StyledTextField
                    {...props}
                    fullWidth
                    placeholder={t("contact.emailPlaceholder")}
                    error={!!errors.email}
                    helperText={errors?.email?.message}
                  />
                )}
              />
              <Controller
                name="subject"
                control={control}
                rules={{
                  required: true,
                }}
                render={(props) => (
                  <StyledTextField
                    {...props}
                    fullWidth
                    placeholder={t("contact.subjectPlaceholder")}
                    error={!!errors.subject}
                    helperText={errors?.subject?.message}
                  />
                )}
              />
              <Controller
                name="contents"
                control={control}
                rules={{
                  required: true,
                }}
                render={(props) => (
                  <StyledTextField
                    {...props}
                    fullWidth
                    multiline
                    minRows={10}
                    maxRows={10}
                    placeholder={t("contact.contentPlaceholder")}
                    error={!!errors.contents}
                    helperText={errors?.contents?.message}
                  />
                )}
              />
              <Button type="submit" id="Company_Contact_Submit">
                {t("contact.submit")}
              </Button>
              <p
                css={`
                  font-size: 14px;
                  color: #7d7d7d;
                `}
              >
                {t("contact.disclaimer")}
              </p>
            </form>
          </Flex>
        </ContentArea>
      </Section>
    </PageLayout>
  );
};

export default About;

const Flex = styled("div")`
  display: flex;
`;

const StyledTextField = styled(TextField)`
  &&& {
    margin-bottom: 10px;
  }
`;
